import { Button, Flex, useMantineTheme } from "@mantine/core";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  AGGridDefault,
  Pagination,
  SegmentControlRounded,
  Select,
  TagCapsules,
} from "../../../components";
import { EOrderSide, IQueryOrder } from "../../../interfaces/IQuery";
import { useQueryClient } from "@tanstack/react-query";
import { useMediaQuery } from "@mantine/hooks";
import { optionsTree, optionsTwo } from "../mock";
import MultiSelectProducts from "../components/MultiSelectProducts";
import CustomSegmentedControl from "../components/CustomSegmetendControl";
import ButtonExport from "../components/ButtonExport";
import { getOrderDownload, useOrder } from "../../../hooks/useOrders";
import { ERole } from "../../../interfaces/User/newUser";
import { useAuth } from "../../../context/auth";
import { useNotificationControl } from "../../../context/notificationContext";
import {
  convertToDateAndHour,
  countdownDate,
  endDateToApiString,
  startDateToApiString,
} from "../../../utils/date";
import { useTranslation } from "react-i18next";
import HeadingOperation from "../components/HeadingOperation";
import ModalRangeDate from "../components/ModalRangeDate";
import ModalDetails from "./ModalDetails";
import { ColDef } from "ag-grid-community";
import dataProcessing from "../../../utils/dataProcessing";
import { IColor } from "../../../utils/selectColors";
import { ReceiptSearch } from "iconsax-react";
import { client_selected } from "../../../client";
import { orderStatusInfo } from "../../../utils/OrderStatusInfo";
import ModalReject from "../components/ModalReject";
import { IOrder } from "../../../interfaces/Order";

export default function OperationOrders() {
  const theme = useMantineTheme();
  const { t } = useTranslation();
  const { roleSelected, user } = useAuth();
  const userCreatedAt = user?.created_at;
  const isAnalytic = roleSelected === ERole.analyst;
  const isLargerThan780 = useMediaQuery("(min-width: 780px)");
  const { notificationControl } = useNotificationControl();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const id = searchParams.get("productId");
  const positionId = searchParams.get("position_id");
  const dateStart = searchParams.get("open_date_start");
  const dateEnd = searchParams.get("open_date_end");
  const queryClient = useQueryClient();
  const [isLoadingDownload, setIsLoadingDownload] = useState<boolean>(false);
  const [IdDetails, setIdDetails] = useState<string>("");
  const [openModalError, setOpenModalError] = useState({} as IOrder);

  function dataRole() {
    return [
      {
        name: ERole.analyst,
        days: 30 - 1,
        isSelected: roleSelected === ERole.analyst,
      },
      {
        name: ERole.client,
        days: countdownDate(userCreatedAt),
        isSelected: roleSelected === ERole.client,
      },
    ];
  }
  const dataRoleSelected =
    dataRole().find((d) => d.isSelected) || dataRole()[1];

  const defaultQuery: IQueryOrder = {
    page: 1,
    pageSize: 10,
    open_date_start: new Date(
      new Date().setDate(new Date().getDate() - dataRoleSelected.days)
    ),
    open_date_end: new Date(),
    roleSelected: roleSelected,
    productId: !!id ? [Number(id)] : undefined,
  };

  const [query, setQuery] = useState<IQueryOrder>({
    ...defaultQuery,
    position_id: !!positionId ? Number(positionId) : undefined,
    open_date_start: !!dateStart
      ? new Date(dateStart)
      : defaultQuery.open_date_start,
    open_date_end: !!dateEnd ? new Date(dateEnd) : defaultQuery.open_date_end,
    productId: !!id ? [Number(id)] : undefined,
  });

  useEffect(() => {
    setQuery((prev) => ({
      ...prev,
      roleSelected: roleSelected,
      open_date_start: new Date(
        new Date().setDate(new Date().getDate() - countdownDate(userCreatedAt))
      ),
    }));
  }, [roleSelected]);

  useEffect(() => {
    const newSearchParams = new URLSearchParams();

    Object.entries(query).forEach(([key, value]) => {
      if (!value) return;
      if (key === "open_date_start") {
        const str = startDateToApiString(value as Date);
        if (str) newSearchParams.set(key, str);
      } else if (key === "open_date_end") {
        const str = endDateToApiString(value as Date);
        if (str) newSearchParams.set(key, str);
      } else if (Array.isArray(value)) {
        newSearchParams.set(key, value.join(","));
      } else {
        newSearchParams.set(key, String(value));
      }
    });

    navigate(`/operacoes/ordens?${newSearchParams.toString()}`, {
      replace: true,
    });

    if (searchParams.size === 0) {
      navigate(`/operacoes/ordens?${newSearchParams.toString()}`, {
        replace: true,
      });
    }
  }, [query, navigate, searchParams]);

  const { data: dataOrderPosition, isLoading, isFetching } = useOrder(query);
  // if (isError && isFetched && !data) return <PageNotFound />;

  const handleUpdate = () => {
    queryClient.refetchQueries({
      queryKey: [
        "order",
        query.order_status,
        query.order_side,
        query.is_open,
        query.page,
        query.pageSize,
        query.productId,
        query.orderBy,
        query.sortOrder,
        query.open_date_start,
        query.open_date_end,
        query.client_id,
      ],
    });
  };

  const handleSelectIsOpen = (value: string) => {
    setQuery((prevQuery) => ({ ...prevQuery, is_open: value }));
  };

  const handleSelectOrderSide = (value: string) => {
    setQuery((prevQuery) => ({ ...prevQuery, order_side: value }));
  };

  const handleExportTable = async () => {
    setIsLoadingDownload(true);

    try {
      await getOrderDownload(query);
    } catch (error) {
      // console.log("🚀 ~ error:", error);
      notificationControl({
        message: "Problema ao fazer o download",
        type: "danger",
      });
    } finally {
      setIsLoadingDownload(false);
    }
  };

  const handleClearFilters = () => {
    setQuery((prev) => ({ ...defaultQuery, productId: prev.productId }));
  };

  const handleOpenDetailsFromRow = (id: string) => {
    setIdDetails(id ? id : "");
  };

  const handleCloseModalError = () => {
    setOpenModalError({} as IOrder);
  };

  const startDate = query.open_date_start
    ? new Date(query.open_date_start)
    : new Date();
  const endDate = query.open_date_end
    ? new Date(query.open_date_end)
    : new Date();

  const dateDifference =
    (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24);

  const disabledExportTable =
    isLoadingDownload || dateDifference > dataRoleSelected?.days;

  const pagination = {
    page: query.page,
    pageSize: query.pageSize,
    totalElements: dataOrderPosition?.totalElements,
    totalPages: dataOrderPosition?.totalPages,
    view: dataOrderPosition?.data?.length || 0,
  };

  const columnDefs = useMemo<ColDef<any>[]>(
    () => [
      {
        headerName: t("operacoes.ordens.table.table_head.product_title"),
        field: "product_title",
        suppressColumnsToolPanel: true,
        width: 200,
        cellRenderer: (params: any) =>
          dataProcessing(params.data?.product_title),
      },
      {
        headerName: t("operacoes.ordens.table.table_head.symbol"),
        field: "symbol",
        suppressColumnsToolPanel: true,
        width: 120,
        cellRenderer: (params: any) => dataProcessing(params.data?.symbol),
      },
      {
        headerName: t("operacoes.ordens.table.table_head.order_status"),
        field: "order_status",
        suppressColumnsToolPanel: true,
        width: 130,
        cellRenderer: (params: any) => {
          const orderStatus = orderStatusInfo(params.data?.order_status);
          return params.data?.order_status ? (
            <TagCapsules mr={12} border colorTag={orderStatus.color}>
              {t(orderStatus.label)}
            </TagCapsules>
          ) : (
            "---"
          );
        },
      },
      {
        headerName: t("operacoes.ordens.table.table_head.order_side"),
        field: "order_side",
        suppressColumnsToolPanel: true,
        width: 110,
        cellRenderer: (params: any) => {
          return params.data?.order_side_text ? (
            <TagCapsules
              mr={12}
              border
              colorTag={
                params.data?.order_side === EOrderSide.BUY
                  ? IColor.orange
                  : IColor.green
              }
            >
              {params.data?.order_side_text}
            </TagCapsules>
          ) : (
            "---"
          );
        },
      },
      {
        headerName: t("operacoes.ordens.table.table_head.quantity"),
        field: "quantity",
        suppressColumnsToolPanel: true,
        width: 160,
        cellRenderer: (params: any) => dataProcessing(params.data?.quantity),
      },
      {
        headerName: t("operacoes.ordens.table.table_head.executed_price"),
        field: "executed_price",
        suppressColumnsToolPanel: true,
        width: 160,
        cellRenderer: (params: any) =>
          dataProcessing(params.data?.executed_price),
      },
      {
        headerName: t("operacoes.ordens.table.table_head.average_price"),
        field: "average_price",
        suppressColumnsToolPanel: true,
        width: 160,
        cellRenderer: (params: any) =>
          dataProcessing(params.data?.average_price),
      },
      {
        headerName: t("operacoes.ordens.table.table_head.partial_profit_loss"),
        field: "partial_profit_loss",
        suppressColumnsToolPanel: true,
        width: 100,
        cellRenderer: (params: any) =>
          dataProcessing(params.data?.partial_profit_loss),
      },
      {
        headerName: t("operacoes.ordens.table.table_head.profit_loss"),
        field: "profit_loss",
        suppressColumnsToolPanel: true,
        width: 100,
        cellRenderer: (params: any) => dataProcessing(params.data?.profit_loss),
      },
      {
        headerName: t("operacoes.ordens.table.table_head.clorderid"),
        field: "clorderid",
        suppressColumnsToolPanel: true,
        width: 155,
        cellRenderer: (params: any) => dataProcessing(params.data?.clorderid),
      },
      {
        headerName: t("operacoes.ordens.table.table_head.position_id"),
        field: "position_seq_num",
        suppressColumnsToolPanel: true,
        width: 70,
        cellRenderer: (params: any) =>
          dataProcessing(params.data?.position_seq_num),
      },
      {
        headerName: t("operacoes.ordens.table.table_head.side_opening"),
        field: "side_opening",
        suppressColumnsToolPanel: true,
        width: 110,
        cellRenderer: (params: any) => {
          return params.data?.side_opening_text ? (
            <TagCapsules
              mr={12}
              border
              colorTag={
                params.data?.side_opening === EOrderSide.BUY
                  ? IColor.orange
                  : IColor.green
              }
            >
              {params.data?.side_opening_text}
            </TagCapsules>
          ) : (
            "---"
          );
        },
      },
      {
        headerName: t("operacoes.ordens.table.table_head.organization_name"),
        field: "organization_name",
        suppressColumnsToolPanel: true,
        width: 100,
        cellRenderer: (params: any) =>
          dataProcessing(params.data?.organization_name),
      },
      {
        headerName: t("operacoes.ordens.table.table_head.sending_time"),
        field: "sending_time",
        suppressColumnsToolPanel: true,
        width: 160,
        cellRenderer: (params: any) =>
          convertToDateAndHour(params.data?.sending_time),
      },
      {
        headerName: t("operacoes.ordens.table.table_head.execution_time"),
        field: "execution_time",
        suppressColumnsToolPanel: true,
        width: 160,
        cellRenderer: (params: any) =>
          convertToDateAndHour(params.data?.execution_time),
      },
      {
        headerName: t("operacoes.details") || "Detalhes",
        colId: "no_filter_details",
        pinned: "right",
        editable: false,
        suppressMovable: true,
        width: 90,
        onCellClicked: (e: any) => {
          const idDetail = isAnalytic ? e?.data?.id : e?.data?.order_analyst_id;
          const idFormatted = idDetail?.toString();
          return handleOpenDetailsFromRow(idFormatted);
        },
        cellRenderer: () => (
          <ReceiptSearch size="30" color={client_selected.colors.primary[1]} />
        ),
        sortable: false,
      },
    ],
    [t]
  );

  const handleRowClick = (row: any) => {
    const hasError =
      row?.order_status?.includes("REJECTED") && !!row?.reject_desc;
    if (hasError) setOpenModalError(row);
  };

  return (
    <Flex
      justify="space-between"
      align="center"
      bg="basic.0"
      p={{ base: "12px", 991: 32 }}
      gap="24px"
      wrap={{ base: "wrap" }}
      sx={{ borderRadius: "12px 12px 0 0" }}
      direction={"column"}
    >
      <Flex
        direction={"column"}
        gap={24}
        justify="space-between"
        align="start"
        w="100%"
      >
        <Flex
          w="100%"
          gap={24}
          direction={{ base: "column", 1700: "column" }}
          justify={"space-between"}
          align={"start"}
        >
          <Flex
            w="100%"
            gap={24}
            direction={{ base: "column", 780: "row" }}
            justify={"space-between"}
            align={"start"}
          >
            <HeadingOperation
              lastUpdated={dataOrderPosition?.lastUpdated}
              handleUpdate={handleUpdate}
              title={t("operacoes.ordens.title")}
            />
            {isAnalytic ? null : (
              <ButtonExport
                isLoadingDownload={isLoadingDownload}
                disabledExportTable={disabledExportTable}
                onClick={handleExportTable}
                days={dataRoleSelected.days}
              />
            )}
          </Flex>
          <Flex
            w="100%"
            gap={24}
            wrap={{ base: "wrap", 1700: "nowrap" }}
            justify={{ base: "space-between", 1700: "end" }}
            align={"end"}
          >
            <ModalRangeDate query={query} setQuery={setQuery} />
            {isLargerThan780 ? (
              <SegmentControlRounded
                data={optionsTwo}
                value={query.is_open}
                label={t("operacoes.ordens.order_status")}
                onChange={handleSelectIsOpen}
              />
            ) : (
              <Select
                data={optionsTwo}
                value={query.is_open || ""}
                placeholder={t("operacoes.ordens.order_status")}
                label={t("operacoes.ordens.order_status")}
                onChange={handleSelectIsOpen}
                w="100%"
              />
            )}
            {isLargerThan780 ? (
              <SegmentControlRounded
                data={optionsTree}
                value={query.order_side}
                label={t("operacoes.ordens.order_side")}
                onChange={handleSelectOrderSide}
              />
            ) : (
              <Select
                data={optionsTree}
                value={query.order_side || ""}
                placeholder={t("operacoes.ordens.order_side")}
                label={t("operacoes.ordens.order_side")}
                onChange={handleSelectOrderSide}
                w="100%"
              />
            )}
          </Flex>
        </Flex>
        <Flex
          gap={24}
          wrap={{ base: "wrap", 1250: "nowrap" }}
          justify="space-between"
          align={"end"}
          w={"100%"}
        >
          <CustomSegmentedControl query={query} setQuery={setQuery} />
          <MultiSelectProducts query={query} setQuery={setQuery} />
          {/* {roleSelected === ERole.analyst ? (
            <FilterClient query={query} setQuery={setQuery} />
          ) : null} */}

          <Button
            variant="primaryTransparent"
            w="100%"
            maw={{ base: "100%", 780: "fit-content" }}
            h="40px"
            onClick={handleClearFilters}
            loaderProps={{ color: theme.colors.primary[1] }}
          >
            {t("global.button.clean_filters")}
          </Button>
        </Flex>
      </Flex>
      <AGGridDefault
        title={
          isAnalytic
            ? t("operacoes.ordens.table.orders_your_products")
            : t("operacoes.ordens.table.orders_copied")
        }
        rowData={dataOrderPosition?.data || []}
        columnDefs={columnDefs}
        isLoading={isLoading || isFetching}
        pagination={
          <Pagination
            pagination={pagination}
            onChange={(page) => setQuery({ ...query, page: page })}
          />
        }
        onRowClick={handleRowClick}
        storageKey={`columns-orders-${isAnalytic ? "analyst" : "customer"}`}
        context={{ query, setQuery, hasFilters: true }}
      />
      {!!IdDetails ? (
        <ModalDetails
          query={query}
          id={IdDetails}
          setClose={() => setIdDetails("")}
        />
      ) : null}
      <ModalReject
        handleCloseModalError={handleCloseModalError}
        clorderid={openModalError.clorderid}
        help_reject_url={openModalError.help_reject_url}
        reject_desc={openModalError.reject_desc}
      />{" "}
    </Flex>
  );
}
